import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Heading, Text } from '@/components/ui/typography';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { semanticTokens } from '@packages/config/theme';
import { cn } from '@/lib/utils';

interface TransactionListHeaderProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedType: string;
  onTypeChange: (value: string) => void;
  selectedStatus: string;
  onStatusChange: (value: string) => void;
  totalCount: number;
  filteredCount: number;
  showExportButton?: boolean;
  onExport?: () => void;
}

export function TransactionListHeader({
  searchTerm,
  onSearchChange,
  selectedType,
  onTypeChange,
  selectedStatus,
  onStatusChange,
  totalCount,
  filteredCount,
  showExportButton = true,
  onExport,
}: TransactionListHeaderProps) {
  const hasFilters = searchTerm || selectedType !== 'all' || selectedStatus !== 'all';
  const showFilters = hasFilters || totalCount > 0;

  return (
    <div className={cn(
      "flex flex-col gap-4",
      semanticTokens.spacing.lg
    )}>
      {/* Title & Export */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex flex-col gap-1">
          <Heading level={3}>
            Transaction History
          </Heading>
          {totalCount > 0 && (
            <Text variant="caption" color="muted">
              {filteredCount === totalCount 
                ? `${totalCount} transaction${totalCount !== 1 ? 's' : ''}`
                : `${filteredCount} of ${totalCount} transaction${totalCount !== 1 ? 's' : ''}`
              }
            </Text>
          )}
        </div>

        {showExportButton && totalCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            className={cn(
              "min-h-[44px] w-full sm:w-auto",
              semanticTokens.motion.standard,
              semanticTokens.focus.ring,
              "focus-visible:outline-none"
            )}
            aria-label="Export transactions"
          >
            <SFIcon icon={sfIcons.download} className="w-4 h-4 mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">Export</span>
          </Button>
        )}
      </div>

      {/* Search & Filters */}
      {showFilters && (
        <div className={cn(
          "flex flex-col sm:flex-row gap-3",
          semanticTokens.spacing.sm.replace('p-', 'gap-')
        )}>
          {/* Search */}
          <div className="relative flex-1">
            <SFIcon
              icon={sfIcons.search}
              className={cn(
                "w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2",
                semanticTokens.fg.muted
              )}
              aria-hidden="true"
            />
            <Input
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className={cn(
                "pl-10 min-h-[44px]",
                semanticTokens.motion.standard,
                semanticTokens.focus.ring,
                "focus-visible:outline-none"
              )}
              aria-label="Search transactions"
            />
          </div>

          {/* Type Filter */}
          <select
            value={selectedType}
            onChange={(e) => onTypeChange(e.target.value)}
            className={cn(
              "px-3 py-2 min-h-[44px]",
              semanticTokens.border.default,
              semanticTokens.radius.md,
              semanticTokens.typography.body,
              semanticTokens.bg.surface,
              semanticTokens.motion.standard,
              semanticTokens.focus.ring,
              "focus-visible:outline-none"
            )}
            aria-label="Filter by transaction type"
          >
            <option value="all">All Types</option>
            <option value="deposit">Deposits</option>
            <option value="withdraw">Withdrawals</option>
            <option value="liquidation">Liquidations</option>
            <option value="transfer">Transfers</option>
            <option value="return">Returns</option>
          </select>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => onStatusChange(e.target.value)}
            className={cn(
              "px-3 py-2 min-h-[44px]",
              semanticTokens.border.default,
              semanticTokens.radius.md,
              semanticTokens.typography.body,
              semanticTokens.bg.surface,
              semanticTokens.motion.standard,
              semanticTokens.focus.ring,
              "focus-visible:outline-none"
            )}
            aria-label="Filter by transaction status"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="processing">Processing</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      )}

      {/* Active Filters Summary */}
      {hasFilters && (
        <div className={cn(
          "flex items-center gap-2 flex-wrap",
          semanticTokens.spacing.sm.replace('p-', 'gap-')
        )}>
          <SFIcon icon={sfIcons.filter} className={cn("w-4 h-4", semanticTokens.fg.muted)} aria-hidden="true" />
          <span className={cn(
            semanticTokens.typography.caption,
            semanticTokens.fg.muted
          )}>
            Active filters:
          </span>
          {searchTerm && (
            <span className={cn(
              semanticTokens.typography.caption,
              semanticTokens.info.bg,
              semanticTokens.info.fg,
              "px-2 py-1 rounded",
              semanticTokens.radius.sm
            )}>
              Search: "{searchTerm}"
            </span>
          )}
          {selectedType !== 'all' && (
            <span className={cn(
              semanticTokens.typography.caption,
              semanticTokens.info.bg,
              semanticTokens.info.fg,
              "px-2 py-1 rounded capitalize",
              semanticTokens.radius.sm
            )}>
              Type: {selectedType}
            </span>
          )}
          {selectedStatus !== 'all' && (
            <span className={cn(
              semanticTokens.typography.caption,
              semanticTokens.info.bg,
              semanticTokens.info.fg,
              "px-2 py-1 rounded capitalize",
              semanticTokens.radius.sm
            )}>
              Status: {selectedStatus}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

